import { format, isValid, Locale, parse } from "date-fns"
import { enUS } from "date-fns/locale"
import { Calendar as CalendarIcon, X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export interface DatePickerProps {
    value?: Date | string
    onValueChange?: (date: Date | undefined) => void
    placeholder?: string
    format?: string
    disabled?: boolean
    className?: string
    inputClassName?: string
    calendarClassName?: string
    showClearButton?: boolean
    locale?: Locale
    minDate?: Date
    maxDate?: Date
    disabledDates?: Date[]
}

const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
    ({
        value,
        onValueChange,
        placeholder = "Select date...",
        format: dateFormat = "yyyy/MM/dd",
        disabled = false,
        className,
        inputClassName,
        calendarClassName,
        showClearButton = true,
        locale = enUS,
        minDate,
        maxDate,
        disabledDates,
    }, ref) => {
        const [open, setOpen] = React.useState(false)
        const [inputValue, setInputValue] = React.useState("")
        const [isValidInput, setIsValidInput] = React.useState(true)
        const [isTyping, setIsTyping] = React.useState(false)

        // Helper function to convert value to Date
        const getDateValue = (val: Date | string | undefined): Date | undefined => {
            if (!val) return undefined
            if (val instanceof Date) return val
            if (typeof val === 'string') {
                // Try to parse the string as a date
                const parsedDate = parse(val, dateFormat, new Date(), { locale })
                return isValid(parsedDate) ? parsedDate : undefined
            }
            return undefined
        }

        // Get the current date value for internal use
        const currentDateValue = getDateValue(value)

        // Helper function to validate and parse input
        const validateAndParseInput = () => {
            if (inputValue.trim() === "") {
                onValueChange?.(undefined)
                setIsValidInput(true)
                return
            }

            const parsedDate = parse(inputValue, dateFormat, new Date(), { locale })

            if (isValid(parsedDate)) {
                // Check if date is within allowed range
                const isInRange = (!minDate || parsedDate >= minDate) &&
                    (!maxDate || parsedDate <= maxDate)

                // Check if date is not in disabled dates
                const isNotDisabled = !disabledDates?.some(disabledDate =>
                    format(disabledDate, "yyyy-MM-dd") === format(parsedDate, "yyyy-MM-dd")
                )

                if (isInRange && isNotDisabled) {
                    onValueChange?.(parsedDate)
                    setIsValidInput(true)
                } else {
                    setIsValidInput(false)
                }
            } else {
                setIsValidInput(false)
            }
        }

        // Update input value when value prop changes
        React.useEffect(() => {
            if (currentDateValue) {
                setInputValue(format(currentDateValue, dateFormat, { locale }))
                setIsValidInput(true)
            } else {
                setInputValue("")
            }
        }, [value, dateFormat, locale, currentDateValue])

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setInputValue(newValue)
            setIsTyping(true)

            if (newValue.trim() === "") {
                onValueChange?.(undefined)
                setIsValidInput(true)
                return
            }

            // Don't parse while typing - just update the input value
            // Validation will happen on blur or enter key
            setIsValidInput(true)
        }

        const handleInputFocus = () => {
            // Don't open calendar when focusing on input for manual typing
            setIsTyping(true)
        }

        const handleInputBlur = () => {
            // Parse and validate the input value when user finishes typing
            validateAndParseInput()

            // Delay to allow for clicking on calendar
            setTimeout(() => {
                setIsTyping(false)
            }, 200)
        }

        const handleCalendarSelect = (date: Date | undefined) => {
            if (date) {
                onValueChange?.(date)
                setInputValue(format(date, dateFormat, { locale }))
                setIsValidInput(true)
                setIsTyping(false)
            }
            setOpen(false)
        }

        const handleClear = () => {
            onValueChange?.(undefined)
            setInputValue("")
            setIsValidInput(true)
            setIsTyping(false)
        }

        const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault()

                // Parse and validate the input value when user presses Enter
                validateAndParseInput()

                setOpen(false)
                setIsTyping(false)
            }
        }

        const handleCalendarButtonClick = () => {
            setOpen(!open)
            setIsTyping(false)
        }

        return (
            <div ref={ref} className={cn("relative", className)}>
                <div className="relative">
                    <Input
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={cn(
                            "pr-10",
                            !isValidInput && "border-destructive focus-visible:ring-destructive",
                            inputClassName
                        )}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {showClearButton && currentDateValue && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 hover:bg-destructive hover:text-destructive-foreground"
                                onClick={handleClear}
                                disabled={disabled}
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        )}
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={handleCalendarButtonClick}
                                    disabled={disabled}
                                >
                                    <CalendarIcon className="h-3 w-3" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className={cn("w-auto p-0", calendarClassName)} align="start">
                                <Calendar
                                    mode="single"
                                    selected={currentDateValue}
                                    onSelect={handleCalendarSelect}
                                    disabled={disabledDates}
                                    fromDate={minDate}
                                    toDate={maxDate}
                                    defaultMonth={currentDateValue}
                                    initialFocus
                                    locale={locale}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
                {!isValidInput && inputValue && (
                    <p className="mt-1 text-red-500" style={{ fontSize: '0.58rem' }}>
                        Invalid date or outside allowed range
                    </p>
                )}
            </div>
        )
    }
)

DatePicker.displayName = "DatePicker"

export { DatePicker }

